import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { fetchUpiSettings, buildUpiPayUrl } from '../utils/upiPayment';
import { fetchPaymentLinksSettings } from '../utils/paymentLinks';
import { initiatePaytmTransaction, submitPaytmPaymentForm } from '../utils/paytmPayment';
import {
  ArrowLeftIcon,
  ClockIcon,
  CheckCircleIcon,
  TruckIcon,
  XCircleIcon,
  MapPinIcon,
  CreditCardIcon,
  CubeIcon
} from '@heroicons/react/24/outline';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upiSettings, setUpiSettings] = useState({ loading: true, error: '', data: null });
  const [paymentLinks, setPaymentLinks] = useState({ loading: true, error: '', data: null });
  const [creatingPaymentLink, setCreatingPaymentLink] = useState(false);
  const [polling, setPolling] = useState({ active: false, message: '', error: '' });
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const pollTimerRef = useRef(null);
  const pollInFlightRef = useRef(false);
  const pollStartedRef = useRef(false);
  const { formatPrice } = useCurrency();
  const { isAuthenticated, getToken } = useAuth();
  const API_BASE = process.env.REACT_APP_API_URL || '';

  const shouldAutoPoll = new URLSearchParams(location.search).get('pay') === '1';
  const paymentMethod = String(order?.payment_method || '').toLowerCase();
  const paymentStatusUpper = String(order?.payment_status || '').toUpperCase();
  const isUpiLikeMethod = ['gpay', 'google_pay', 'upi', 'upi_intent'].includes(paymentMethod);
  const isPaytmMethod = paymentMethod === 'paytm';
  const paymentLinksEnabled = Boolean(paymentLinks.data?.enabled);

  useEffect(() => {
    let cancelled = false;
    const loadUpiSettings = async () => {
      try {
        setUpiSettings({ loading: true, error: '', data: null });
        const data = await fetchUpiSettings({ apiBase: API_BASE });
        if (!cancelled) setUpiSettings({ loading: false, error: '', data });
      } catch (err) {
        if (!cancelled) {
          setUpiSettings({ loading: false, error: err?.message || 'Failed to load payment settings', data: null });
        }
      }
    };
    loadUpiSettings();
    return () => { cancelled = true; };
  }, [API_BASE]);

  useEffect(() => {
    let cancelled = false;
    const loadPaymentLinks = async () => {
      try {
        setPaymentLinks({ loading: true, error: '', data: null });
        const data = await fetchPaymentLinksSettings({ apiBase: API_BASE });
        if (!cancelled) setPaymentLinks({ loading: false, error: '', data });
      } catch (err) {
        if (!cancelled) {
          setPaymentLinks({ loading: false, error: err?.message || 'Failed to load payment options', data: null });
        }
      }
    };
    loadPaymentLinks();
    return () => { cancelled = true; };
  }, [API_BASE]);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollInFlightRef.current = false;
    setPolling((prev) => ({ ...prev, active: false }));
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = () => {
    if (pollTimerRef.current) return;

    let attempts = 0;
    setPolling({ active: true, message: 'Waiting for payment confirmation…', error: '' });

    pollTimerRef.current = setInterval(async () => {
      if (pollInFlightRef.current) return;
      pollInFlightRef.current = true;
      attempts += 1;

      try {
        const token = getToken(false);
        if (!token) {
          stopPolling();
          return;
        }

        const res = await fetch(`${API_BASE}/api/orders/${id}/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const json = await res.json();
        if (!res.ok || !json?.success) {
          throw new Error(json?.message || 'Failed to check payment status');
        }

        const status = String(json?.data?.payment_status || '').toUpperCase();
        setOrder((prev) => (prev ? { ...prev, payment_status: status } : prev));

        if (status === 'PAID') {
          stopPolling();
          toast.success('Payment confirmed ✅');
          await fetchOrder();
          return;
        }

        if (attempts >= 120) {
          stopPolling();
          setPolling({ active: false, message: '', error: 'Payment not confirmed yet. If you completed payment, please wait a moment and refresh.' });
        }
      } catch (err) {
        stopPolling();
        setPolling({ active: false, message: '', error: err?.message || 'Payment status check failed' });
      } finally {
        pollInFlightRef.current = false;
      }
    }, 2500);
  };

  useEffect(() => {
    if (!shouldAutoPoll) return;
    if (!paymentLinksEnabled) return;
    if (!order) return;
    if (!isUpiLikeMethod) return;
    if (paymentStatusUpper === 'PAID') return;
    if (pollStartedRef.current) return;

    pollStartedRef.current = true;
    startPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoPoll, paymentLinksEnabled, order, isUpiLikeMethod, paymentStatusUpper]);

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken(false); // E-commerce token
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${API_BASE}/api/orders/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setOrder(data.data.order);
      } else {
        toast.error(data.message || 'Failed to fetch order details');
        navigate('/orders');
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load order details. Please try again.');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, getToken, id, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrder();
    }
  }, [fetchOrder, isAuthenticated]);

  const getStatusIcon = (status) => {
    const statusUpper = (status || '').toUpperCase();
    switch (statusUpper) {
      case 'PENDING':
        return <ClockIcon className="w-6 h-6 text-yellow-600" />;
      case 'CONFIRMED':
        return <CheckCircleIcon className="w-6 h-6 text-blue-600" />;
      case 'PACKING':
        return <CubeIcon className="w-6 h-6 text-purple-600" />;
      case 'SHIPPED':
        return <TruckIcon className="w-6 h-6 text-indigo-600" />;
      case 'OUT_FOR_DELIVERY':
        return <TruckIcon className="w-6 h-6 text-orange-600" />;
      case 'DELIVERED':
        return <CheckCircleIcon className="w-6 h-6 text-green-600" />;
      case 'CANCELLED':
        return <XCircleIcon className="w-6 h-6 text-red-600" />;
      default:
        return <ClockIcon className="w-6 h-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    const statusUpper = (status || '').toUpperCase();
    switch (statusUpper) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'CONFIRMED':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'PROCESSING':
        return 'bg-cyan-100 text-cyan-800 border-cyan-300';
      case 'PACKING':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'SHIPPED':
      case 'DISPATCHED':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 'OUT_FOR_DELIVERY':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'DELIVERED':
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'CANCELLED':
      case 'RETURNED':
      case 'REFUNDED':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusTimeline = (status) => {
    const statusUpper = (status || 'PENDING').toUpperCase();
    const statuses = [
      { key: 'PENDING', label: 'Order Placed', icon: '✅' },
      { key: 'CONFIRMED', label: 'Confirmed', icon: '✅' },
      { key: 'PROCESSING', label: 'Processing', icon: '✅' },
      { key: 'PACKING', label: 'Packed', icon: '✅' },
      { key: 'SHIPPED', label: 'Shipped', icon: '✅' },
      { key: 'DISPATCHED', label: 'Dispatched', icon: '✅' },
      { key: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', icon: '🔄' },
      { key: 'DELIVERED', label: 'Delivered', icon: '✅' },
      { key: 'COMPLETED', label: 'Completed', icon: '✅' }
    ];
    
    const currentIndex = statuses.findIndex(s => s.key === statusUpper);
    const isCancelled = statusUpper === 'CANCELLED' || statusUpper === 'RETURNED' || statusUpper === 'REFUNDED';
    
    return statuses.map((step, index) => {
      const isCompleted = index <= currentIndex && currentIndex >= 0 && !isCancelled;
      const isCurrent = index === currentIndex && !isCancelled;
      
      return {
        ...step,
        isCompleted,
        isCurrent,
        isCancelled: isCancelled && (step.key === 'CANCELLED' || step.key === 'RETURNED' || step.key === 'REFUNDED')
      };
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseShippingAddress = (address) => {
    if (!address) return 'N/A';
    if (typeof address === 'string') {
      try {
        const parsed = JSON.parse(address);
        if (typeof parsed === 'object') {
          return parsed;
        }
      } catch (e) {
        // If it's already a string, return it
        return address;
      }
    }
    return address;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
            <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin absolute top-0 left-0 animate-pulse"></div>
          </div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center bg-white p-12 rounded-2xl shadow-xl max-w-md">
          <CubeIcon className="w-24 h-24 text-gray-300 mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Not Found</h2>
          <p className="text-gray-600 mb-8">The order you're looking for doesn't exist.</p>
          <Link
            to="/orders"
            className="inline-block bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-8 py-4 rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg font-semibold"
          >
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  const shippingAddress = parseShippingAddress(order.shipping_address);
  const orderItems = Array.isArray(order.items) ? order.items : [];
  const orderStatus = order.status || 'pending';
  const orderTotal = order.total_amount || 0;
  const canPayNow =
    isUpiLikeMethod &&
    paymentStatusUpper !== 'PAID' &&
    upiSettings.data?.enabled &&
    Boolean(upiSettings.data?.vpa);

  const handleVerifyPayment = async () => {
    try {
      const token = getToken(false);
      if (!token) { navigate('/login'); return; }
      setVerifyingPayment(true);
      const res = await fetch(`${API_BASE}/api/payments/razorpay/check-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ order_id: order.id })
      });
      const json = await res.json();
      if (!res.ok || !json?.success) throw new Error(json?.message || 'Verification failed');
      if (json?.data?.verified) {
        toast.success('Payment verified ✅ Order confirmed!');
        await fetchOrder();
      } else {
        toast.error(json?.data?.message || 'No completed payment found. If you were charged, contact support.');
      }
    } catch (err) {
      toast.error(err?.message || 'Could not verify payment');
    } finally {
      setVerifyingPayment(false);
    }
  };

  const handlePayWithPaymentLink = async () => {
    try {
      const token = getToken(false);
      if (!token) {
        navigate('/login');
        return;
      }

      setCreatingPaymentLink(true);
      setPolling({ active: false, message: '', error: '' });

      const res = await fetch(`${API_BASE}/api/payments/create-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ order_id: order.id })
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || 'Failed to create payment link');
      }

      if (json?.data?.alreadyPaid) {
        toast.success('Already paid ✅');
        await fetchOrder();
        return;
      }

      const linkUrl = json?.data?.linkUrl;
      if (!linkUrl) {
        throw new Error('Payment link not available');
      }

      const opened = window.open(linkUrl, '_blank', 'noopener,noreferrer');
      if (!opened) {
        window.location.href = linkUrl;
        return;
      }

      startPolling();
    } catch (err) {
      toast.error(err?.message || 'Payment initiation failed');
    } finally {
      setCreatingPaymentLink(false);
    }
  };

  const handlePayWithUpi = () => {
    if (!upiSettings.data?.vpa) return;
    const txRef = String(order.order_number || order.id || '').trim();
    const notePrefix = upiSettings.data?.notePrefix || 'Order';
    const note = `${notePrefix} #${txRef}`;
    const upiUrl = buildUpiPayUrl({
      vpa: upiSettings.data.vpa,
      payeeName: upiSettings.data.payeeName,
      amount: Number(orderTotal) || 0,
      txRef,
      note,
      merchantCode: upiSettings.data.merchantCode,
      websiteUrl: upiSettings.data.websiteUrl
    });

    // UPI intents typically work on mobile devices with a UPI app installed.
    window.location.href = upiUrl;
  };

  const handlePayWithPaytm = async () => {
    try {
      const token = getToken(false);
      if (!token) {
        navigate('/login');
        return;
      }
      const txn = await initiatePaytmTransaction({ apiBase: API_BASE, token, orderId: order.id });
      submitPaytmPaymentForm(txn);
    } catch (err) {
      console.error('Paytm retry failed:', err);
      toast.error(err?.message || 'Paytm payment initiation failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
  <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/orders"
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold mb-4"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Back to Orders
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Order #{order.order_number || order.id}
          </h1>
          <p className="text-gray-600">Placed on {formatDate(order.created_at)}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Order Status */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center gap-4 mb-6">
                {getStatusIcon(orderStatus)}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Order Status</h2>
                  <span
                    className={`inline-block px-4 py-2 rounded-full text-sm font-semibold border mt-2 ${getStatusColor(
                      orderStatus
                    )}`}
                  >
                    {(orderStatus || 'PENDING').replace(/_/g, ' ')}
                  </span>
                </div>
              </div>

              {/* Status Timeline */}
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Order Tracking</h3>
                <div className="space-y-4">
                  {getStatusTimeline(orderStatus).map((step, index) => (
                    <div key={step.key} className="flex items-start gap-4">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                            step.isCompleted
                              ? 'bg-green-500 text-white'
                              : step.isCurrent
                              ? 'bg-yellow-500 text-white animate-pulse'
                              : 'bg-gray-200 text-gray-500'
                          }`}
                        >
                          {step.isCompleted ? '✓' : step.isCurrent ? '●' : index + 1}
                        </div>
                        {index < getStatusTimeline(orderStatus).length - 1 && (
                          <div
                            className={`w-0.5 h-12 mt-2 ${
                              step.isCompleted ? 'bg-green-500' : 'bg-gray-200'
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1 pt-1">
                        <p
                          className={`font-semibold ${
                            step.isCompleted
                              ? 'text-green-700'
                              : step.isCurrent
                              ? 'text-yellow-700'
                              : 'text-gray-500'
                          }`}
                        >
                          {step.label}
                        </p>
                        {step.isCurrent && (
                          <p className="text-xs text-gray-500 mt-1">In progress...</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {order.tracking_number && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Tracking Number</p>
                  <p className="font-semibold text-gray-900">{order.tracking_number}</p>
                </div>
              )}
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <CubeIcon className="w-6 h-6 text-indigo-600" />
                Order Items ({orderItems.length})
              </h2>
              <div className="space-y-4">
                {orderItems.length > 0 ? (
                  orderItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <img
                        key={`${item.id}-${item.image_url}`}
                        src={item.image_url || '/api/placeholder/80/80'}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                        loading="lazy"
                        onError={(e) => {
                          if (!e.target.dataset.errorHandled) {
                            e.target.dataset.errorHandled = 'true';
                            e.target.src = '/api/placeholder/80/80';
                          }
                        }}
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{item.name}</h3>
                        {item.description && (
                          <p className="text-sm text-gray-600 line-clamp-2">{item.description}</p>
                        )}
                        <p className="text-sm text-gray-500 mt-1">Quantity: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-gray-900">
                          {formatPrice((item.price || 0) * (item.quantity || 0))}
                        </p>
                        <p className="text-sm text-gray-500">
                          {formatPrice(item.price || 0)} each
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-600 text-center py-8">No items found in this order.</p>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Shipping Address */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MapPinIcon className="w-6 h-6 text-indigo-600" />
                Shipping Address
              </h2>
              <div className="text-gray-700">
                {typeof shippingAddress === 'object' ? (
                  <div className="space-y-1">
                    {shippingAddress.street && <p>{shippingAddress.street}</p>}
                    {shippingAddress.city && shippingAddress.state && (
                      <p>
                        {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip_code}
                      </p>
                    )}
                    {shippingAddress.country && <p>{shippingAddress.country}</p>}
                  </div>
                ) : (
                  <p>{shippingAddress}</p>
                )}
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCardIcon className="w-6 h-6 text-indigo-600" />
                Payment Information
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Method:</span>
                  <span className="font-semibold text-gray-900 capitalize">
                    {order.payment_method?.replace('_', ' ') || 'N/A'}
                  </span>
                </div>
                {order.payment_status && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment Status:</span>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        paymentStatusUpper === 'PAID'
                          ? 'bg-green-100 text-green-800'
                          : paymentStatusUpper === 'FAILED'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {paymentStatusUpper || 'UNPAID'}
                    </span>
                  </div>
                )}

                {isUpiLikeMethod && (
                  <div className="pt-2">
                    {paymentLinks.loading ? (
                      <p className="text-xs text-gray-600">Loading payment options…</p>
                    ) : paymentLinksEnabled ? (
                      <>
                        {paymentStatusUpper !== 'PAID' && (
                          <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
                            <p className="text-sm font-semibold text-amber-900">Payment pending</p>
                            <p className="text-xs text-amber-800 mt-1">
                              Complete the payment in the secure Razorpay/Cashfree window, then return here. This page
                              will confirm automatically once the gateway sends confirmation.
                            </p>
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handlePayWithPaymentLink}
                          disabled={creatingPaymentLink || paymentStatusUpper === 'PAID'}
                          className={`w-full py-3 rounded-xl transition-all font-semibold ${
                            creatingPaymentLink || paymentStatusUpper === 'PAID'
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          {creatingPaymentLink ? 'Opening payment…' : paymentStatusUpper === 'PAID' ? 'Paid' : 'Pay Now'}
                        </button>
                        {polling.active ? (
                          <p className="text-xs text-gray-600 mt-2">{polling.message}</p>
                        ) : polling.error ? (
                          <p className="text-xs text-amber-700 mt-2">{polling.error}</p>
                        ) : paymentStatusUpper !== 'PAID' ? (
                          <p className="text-xs text-gray-600 mt-2">
                            Opens a secure payment link. Payment status updates automatically after confirmation.
                          </p>
                        ) : null}
                        {paymentStatusUpper !== 'PAID' && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-500 mb-2">
                              Already paid but status not updated? Your bank may have been charged while the payment window showed an error.
                            </p>
                            <button
                              type="button"
                              onClick={handleVerifyPayment}
                              disabled={verifyingPayment}
                              className="w-full py-2 rounded-lg border border-indigo-400 text-indigo-700 text-sm font-medium hover:bg-indigo-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {verifyingPayment ? 'Checking with Razorpay…' : 'Already paid? Verify payment'}
                            </button>
                          </div>
                        )}
                      </>
                    ) : canPayNow ? (
                      <>
                        <button
                          type="button"
                          onClick={handlePayWithUpi}
                          className="w-full bg-green-600 text-white py-3 rounded-xl hover:bg-green-700 transition-all font-semibold"
                        >
                          Pay with Google Pay
                        </button>
                        <p className="text-xs text-gray-600 mt-2">
                          Pay to <span className="font-semibold">{upiSettings.data.vpa}</span>
                          {upiSettings.data.payeeName ? (
                            <span> ({upiSettings.data.payeeName})</span>
                          ) : null}
                          . After payment, our team will verify and update the status.
                        </p>
                      </>
                    ) : upiSettings.loading ? (
                      <p className="text-xs text-gray-600">Loading payment options…</p>
                    ) : (
                      <p className="text-xs text-amber-700">
                        Google Pay (UPI) is not available right now. {upiSettings.error ? upiSettings.error : ''}
                      </p>
                    )}
                  </div>
                )}

                {isPaytmMethod && paymentStatusUpper !== 'PAID' && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handlePayWithPaytm}
                      className="w-full bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition-all font-semibold"
                    >
                      Pay with Paytm
                    </button>
                    <p className="text-xs text-gray-600 mt-2">
                      You will be redirected to Paytm. Payment status updates automatically after confirmation.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-gray-700">
                  <span>Subtotal:</span>
                  <span className="font-semibold">
                    {formatPrice(
                      orderItems.reduce((sum, item) => {
                        const price = item.price || 0;
                        const quantity = item.quantity || 0;
                        return sum + (price * quantity);
                      }, 0)
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Shipping:</span>
                  <span className="font-semibold text-green-600">Free</span>
                </div>
                <div className="border-t-2 border-gray-200 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-900">Total:</span>
                    <span className="text-2xl font-bold text-indigo-600">
                      {formatPrice(orderTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  </div>
);
};

export default OrderDetail;
